import React, { useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Menu, Linkedin, ArrowUpRight } from "lucide-react";
import { navItems, ctaItem } from "@/config/navConfig";
import { companyConfig } from "@/config/companyConfig";
import Logo from "./Logo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { NAV } from "@/constants/testIds";

const LOGO_IMAGE_HEIGHT = 50;
const WHITE_LOGO_SRC = "/assets/logos/captec-logo-white.png";

const slugifyLabel = (label) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const NavbarLogo = ({ dataTestid }) => (
  <Logo variant="white" height={LOGO_IMAGE_HEIGHT} dataTestid={dataTestid} />
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
    <header
      className="fixed top-0 inset-x-0 z-50 border-b border-white/10 shadow-[0_18px_60px_rgba(0,0,0,0.38)] transition-all duration-300"
      style={{
        backgroundColor: "rgba(1, 6, 14, 0.96)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
      }}
      role="banner"
    >
      <div className="container-px mx-auto max-w-7xl flex items-center justify-between h-16 lg:h-20">
        <NavbarLogo dataTestid={NAV.logo} />

        <nav aria-label="Principal" className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.type === "external" ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                data-testid={NAV.link(slugifyLabel(item.label))}
                className="group relative px-4 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors duration-200 inline-flex items-center gap-1"
              >
                {item.label}
                <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
              </a>
            ) : (
              <NavLink
                key={item.label}
                to={item.href}
                end={item.href === "/"}
                data-testid={NAV.link(slugifyLabel(item.label))}
                className={({ isActive }) =>
                  cn(
                    "relative px-4 py-2 text-sm font-medium transition-colors duration-200",
                    isActive ? "text-white" : "text-white/70 hover:text-white"
                  )
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    <span
                      className={cn(
                        "absolute -bottom-1.5 left-0 right-0 mx-auto h-[2px] rounded-full transition-all duration-300",
                        isActive
                          ? "w-6 bg-[hsl(var(--cyan))] shadow-[0_0_10px_hsl(var(--cyan))]"
                          : "w-0 bg-[hsl(var(--cyan))]"
                      )}
                    />
                  </span>
                )}
              </NavLink>
            )
          )}

          {companyConfig.linkedinUrl ? (
            <a
              href={companyConfig.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="ml-1 p-2 rounded-md text-white/70 hover:text-[hsl(var(--cyan))] hover:bg-white/5 transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          ) : (
            <span
              aria-disabled="true"
              title="LinkedIn pendiente de configuración"
              className="ml-1 p-2 rounded-md text-white/30 cursor-not-allowed"
            >
              <Linkedin className="w-4 h-4" />
            </span>
          )}

          <Link
            to={ctaItem.href}
            data-testid={NAV.ctaContacto}
            className="ml-3 group relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[hsl(var(--navy-deep))] bg-gradient-cta hover:shadow-glow transition-shadow duration-300"
          >
            <span>{ctaItem.label}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--navy-deep))] group-hover:scale-150 transition-transform" />
          </Link>
        </nav>

        <SheetTrigger asChild><button
          type="button"
          aria-label="Abrir menú"
          aria-expanded={open}
          data-testid={NAV.mobileToggle}
          className="lg:hidden p-2 rounded-md text-white hover:bg-white/5 focus-ring"
        >
          <Menu className="w-6 h-6" />
        </button></SheetTrigger>
      </div>

      <SheetContent side="right" className="mobile-nav-sheet flex flex-col p-0 gap-0 bg-[hsl(var(--navy-deep))] text-white border-white/10" data-testid={NAV.mobileDrawer}>
        <SheetTitle className="sr-only">Navegación CAPTEC</SheetTitle>
        <SheetDescription className="sr-only">Accede a las páginas y al soporte de CAPTEC.</SheetDescription>
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <img
              src={WHITE_LOGO_SRC}
              alt="Captec"
              style={{
                width: 200,
                height: 32,
              }}
              className="object-contain object-left max-w-[70%]"
            />


          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-5 flex flex-col gap-1" aria-label="Móvil">
            {navItems.map((item) =>
              item.type === "external" ? (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid={NAV.mobileLink(slugifyLabel(item.label))}
                  className="flex items-center justify-between px-4 py-3.5 rounded-lg text-white/90 hover:bg-white/5 hover:text-[hsl(var(--cyan))] transition-colors"
                >
                  <span className="text-base font-medium">{item.label}</span>
                  <ArrowUpRight className="w-4 h-4 opacity-70" />
                </a>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.href}
                  end={item.href === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={NAV.mobileLink(slugifyLabel(item.label))}
                  className={({ isActive }) =>
                    cn(
                      "px-4 py-3.5 rounded-lg text-base font-medium transition-colors",
                      isActive
                        ? "bg-white/5 text-[hsl(var(--cyan))]"
                        : "text-white/85 hover:bg-white/5 hover:text-white"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="p-5 border-t border-white/10">
            <Link
              to={ctaItem.href}
              onClick={() => setOpen(false)}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[hsl(var(--navy-deep))] bg-gradient-cta"
            >
              {ctaItem.label}
            </Link>
          </div>
      </SheetContent>
    </header>
    </Sheet>
  );
};

export default Navbar;